import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, User, AlertCircle } from 'lucide-react';
import { AuthLayout } from '@/components/AuthLayout';
import { formatApiErrorDetail } from '@/utils/format-error';

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setIsLoading(true);
    try {
      await register(email, password, name);
      navigate('/');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout heroText="Aggregate Substacks, blogs, podcasts, and Twitter feeds into one unified timeline.">
      <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight uppercase mb-2">Create Account</h2>
      <p className="text-zinc-600 font-medium mb-8">Start aggregating your feeds</p>

      {error && (
        <div data-testid="register-error" className="flex items-center gap-2 bg-red-100 border-2 border-black p-3 mb-6 text-sm font-medium text-red-700">
          <AlertCircle strokeWidth={2.5} size={16} />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-600 mb-2 block">Name</label>
          <div className="relative">
            <User strokeWidth={2.5} size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              data-testid="register-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="brutalist-input pl-10"
              placeholder="Your name"
              required
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-600 mb-2 block">Email</label>
          <div className="relative">
            <Mail strokeWidth={2.5} size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              data-testid="register-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="brutalist-input pl-10"
              placeholder="your@email.com"
              required
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-600 mb-2 block">Password</label>
          <div className="relative">
            <Lock strokeWidth={2.5} size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              data-testid="register-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="brutalist-input pl-10"
              placeholder="Min. 6 characters"
              required
            />
          </div>
        </div>
        <button
          data-testid="register-submit-button"
          type="submit"
          disabled={isLoading}
          className="brutalist-btn-primary w-full text-center disabled:opacity-60"
        >
          {isLoading ? 'Creating account...' : 'Sign Up'}
        </button>
      </form>

      <p className="mt-6 text-center text-sm font-medium text-zinc-600">
        Already have an account?{' '}
        <Link data-testid="go-to-login-link" to="/login" className="text-pink-500 font-bold uppercase hover:underline">
          Sign In
        </Link>
      </p>
    </AuthLayout>
  );
}
